import { Card, Group, Text } from "@mantine/core";
import type { ReactNode } from "react";

/**
 * A bordered panel with an uppercase eyebrow.
 *
 * Borders, never a shadow. The design is border-led: shadows are reserved for
 * things that genuinely float above the page - popovers, bottom sheets, modal
 * sheets - so that when something does cast one, it means "this is on top of
 * what you were doing" rather than "this is a box".
 */
export function SectionCard({
  title,
  action,
  children,
  required = false,
  padding = "lg",
}: {
  title?: string;
  /** A link or button on the header's right - "Edit", "Add part". */
  action?: ReactNode;
  children: ReactNode;
  /**
   * Marks the whole section as required, for a capture step that cannot be
   * skipped. Written as a word rather than an asterisk: an asterisk is a
   * convention people have to already know, and this is read by staff at a
   * customer's gate.
   */
  required?: boolean;
  padding?: string;
}) {
  return (
    <Card withBorder radius="md" padding={padding}>
      {(title || action) && (
        <Group justify="space-between" align="center" mb="md" wrap="nowrap">
          {title && (
            <Group gap={6} wrap="nowrap">
              <Text fz="xs" fw={700} c="gray.6" tt="uppercase" lts="0.04em">
                {title}
              </Text>
              {required && (
                <Text fz="xs" fw={600} c="red.7" tt="uppercase" lts="0.04em">
                  required
                </Text>
              )}
            </Group>
          )}
          {action}
        </Group>
      )}
      {children}
    </Card>
  );
}
